import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { ColumnConfig, CustomerDataField } from "./CustomerColumnConfig";

interface CustomerCardRowHeaderProps {
  columnConfig: ColumnConfig[];
  sortField: CustomerDataField;
  sortDirection: "asc" | "desc";
  onRequestSort: (field: CustomerDataField) => void;
  onSortDirectionChange?: (direction: "asc" | "desc") => void;
  allSelected: boolean;
  indeterminate: boolean;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  totalCount: number;
  selectedCount: number;
}

const CustomerCardRowHeader: React.FC<CustomerCardRowHeaderProps> = ({
  columnConfig,
  sortField,
  sortDirection,
  onRequestSort,
  allSelected,
  indeterminate,
  onSelectAll,
  totalCount,
  selectedCount,
  onSortDirectionChange,
}) => {
  const theme = useTheme();

  const sortedColumnConfig = [...columnConfig].sort(
    (a, b) => (a.priority || 100) - (b.priority || 100)
  );

  // Get sortable columns only
  const sortableColumns = sortedColumnConfig.filter(col => col.sortable !== false);

  const handleSortFieldChange = (event: SelectChangeEvent<CustomerDataField>) => {
    onRequestSort(event.target.value as CustomerDataField);
  };

  const handleSortDirectionChange = (event: SelectChangeEvent<"asc" | "desc">) => {
    const newDirection = event.target.value as "asc" | "desc";
    if (onSortDirectionChange) {
      onSortDirectionChange(newDirection);
    } else {
      // Fallback: if no direction handler, request sort to toggle
      onRequestSort(sortField);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        gap: 2,
        bgcolor: theme.palette.grey[50],
        borderRadius: 1,
        mb: 2,
        borderBottom: `2px solid ${theme.palette.divider}`,
      }}
    >
      {/* Left side - Selection controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Checkbox
            indeterminate={indeterminate}
            checked={allSelected}
            onChange={onSelectAll}
            inputProps={{ "aria-label": "select all customers" }}
            sx={{ p: 0 }}
          />
          {selectedCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
              {selectedCount}/{totalCount}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right side - Sort controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Sort field dropdown */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={sortField}
            onChange={handleSortFieldChange}
            displayEmpty
            variant="outlined"
            sx={{
              "& .MuiSelect-select": {
                py: 1,
              },
            }}
          >
            {sortableColumns.map((column) => (
              <MenuItem key={column.field} value={column.field}>
                {column.headerLabel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort direction dropdown */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={sortDirection}
            onChange={handleSortDirectionChange}
            variant="outlined"
            sx={{
              "& .MuiSelect-select": {
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              },
            }}
          >
            <MenuItem value="asc" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ArrowUpIcon fontSize="small" />
              Stigande
            </MenuItem>
            <MenuItem value="desc" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ArrowDownIcon fontSize="small" />
              Fallande
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default CustomerCardRowHeader;